import { useState } from "react";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main className="pt-20">
      {/* Hero — full bleed image */}
      <section className="relative py-32 px-6 overflow-hidden text-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/90 via-navy/85 to-navy-dark/95" />
        </div>
        <div className="relative max-w-2xl mx-auto">
          <p className="text-gold text-xs tracking-widest uppercase mb-4">Strategy Audit</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-4">
            Let's Talk<br />
            <span className="text-gold">About Your Wealth.</span>
          </h1>
          <span className="gold-line mx-auto" />
          <p className="text-white/55 text-lg">
            Reach out by phone, email, or schedule a meeting — we're ready to help.
          </p>
        </div>
      </section>

      {/* 3-column Call / Meet / Email CTAs */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-widest uppercase mb-2">Connect With Us</p>
            <h2 className="section-title">Call, Meet, or Email</h2>
            <span className="gold-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Call */}
            <div className="group relative rounded-2xl overflow-hidden h-72 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80"
                alt="Call us"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/95 via-navy-dark/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <p className="text-gold text-xs tracking-widest uppercase mb-2">Call</p>
                <p className="text-white font-semibold mb-1">Speak directly with our team.</p>
                <a
                  href="tel:+19546017475"
                  className="inline-block mt-3 btn-gold text-sm px-6 py-2"
                >
                  Call Now
                </a>
              </div>
            </div>

            {/* Meet */}
            <div className="group relative rounded-2xl overflow-hidden h-72 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
                alt="Schedule a meeting"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/95 via-navy-dark/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <p className="text-gold text-xs tracking-widest uppercase mb-2">Meet</p>
                <p className="text-white font-semibold mb-1">Virtual or in-person strategy session.</p>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdcIec_5ByslvqXxOuuPF7U5ytk0-O-WGPq2UTYgipGwOeUOg/viewform?usp=publish-editor"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-3 btn-gold text-sm px-6 py-2"
                >
                  Book a Meeting
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="group relative rounded-2xl overflow-hidden h-72 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80"
                alt="Email us"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/95 via-navy-dark/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <p className="text-gold text-xs tracking-widest uppercase mb-2">Email</p>
                <p className="text-white font-semibold mb-1">Send us a message anytime.</p>
                <a
                  href="mailto:nstephens@experiorfinancialgroup.com"
                  className="inline-block mt-3 btn-gold text-sm px-6 py-2"
                >
                  Email Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple contact form */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Quick Message</p>
            <h2 className="section-title">Send Us a Note</h2>
            <span className="gold-line mx-auto" />
          </div>

          {submitted ? (
            <div className="card-dark text-center py-16">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="font-display text-2xl text-gold mb-2">Message Received</h3>
              <p className="text-gray-600">We'll be in touch within 24–48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Message *</label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-gold transition-colors resize-none"
                  placeholder="Tell us how we can help you..."
                />
              </div>
              <button type="submit" className="btn-gold w-full text-center py-4">
                Send Message
              </button>
              <p className="text-gray-400 text-xs text-center">
                For informational intake only. Does not constitute financial, tax, or legal advice.
              </p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
